/**
 * One row of "What changed since the last review": a loop in play, and the
 * one-sentence summary of what moved since its previous pass.
 *
 * Reads `catchball_loop_diff` (migration 822) through the same hook the ball
 * desk, inbox and timeline use; nothing here recomputes the comparison. A loop
 * with no earlier pass to compare with draws nothing, and tells its parent so
 * the section can stay hidden when no row has anything to say.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoopDiff } from '../../hooks/useLoopDiff';
import { loopDiffHasReference, referenceLine } from '../../lib/catchball/loopDiff';

export interface LoopChangeRowProps {
  loopId: string;
  title: string;
  href: string;
  rowClassName: string;
  onHasChange: (loopId: string, has: boolean) => void;
}

export function LoopChangeRow({ loopId, title, href, rowClassName, onHasChange }: LoopChangeRowProps) {
  const { diff, isLoading } = useLoopDiff({ loopId });
  const has = !isLoading && loopDiffHasReference(diff) && Boolean(diff.sentence);

  useEffect(() => {
    if (!isLoading) onHasChange(loopId, has);
  }, [loopId, has, isLoading, onHasChange]);

  if (!has || !diff) return null;
  return (
    <li>
      <Link to={href} className={rowClassName}>
        <span>
          <span className="text-[var(--text-pri)]">{title}</span>
          <span className="block text-base text-[var(--text-sec)]">{diff.sentence}</span>
          <span className="block text-sm text-[var(--text-ter)]">{referenceLine(diff)}</span>
        </span>
      </Link>
    </li>
  );
}
